/**
 * ProblemDetail -> ErrorPage props mapping (ALL-4857).
 *
 * The Spring/backend half of this workstream (branch
 * claude/browser-error-redirect in the sibling context repo) returns RFC
 * 9457 ProblemDetail JSON for API/XHR failures, with reasonCode,
 * supportReference and httpStatus extension fields:
 *
 *   {
 *     "type": "about:blank",
 *     "title": "...",
 *     "status": 403,
 *     "detail": "...",
 *     "reasonCode": "AUTH_INSUFFICIENT_PERMISSION",
 *     "supportReference": "req_abc123",
 *     "httpStatus": 403
 *   }
 *
 * This module is framework-agnostic (no React/DOM) so it is copied
 * verbatim into every app alongside categories.ts.
 */

import { codes } from "./generated/auth-reason-codes.json";

export interface ProblemDetailBody {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  reasonCode?: string;
  supportReference?: string;
  httpStatus?: number;
  [key: string]: unknown;
}

const AUTH_REASON_CODES = new Set(codes.map(({ name }) => name));
const SUPPORT_REFERENCE = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,95}$/;
export const UNKNOWN_CLIENT_ERROR = "UNKNOWN_CLIENT_ERROR";

export function normalizeReasonCode(value: unknown): string {
  if (typeof value !== "string" || value.length > 128 || value.trim() !== value || /[^\x20-\x7e]/.test(value)) return UNKNOWN_CLIENT_ERROR;
  if (value === UNKNOWN_CLIENT_ERROR || AUTH_REASON_CODES.has(value) || /^HTTP_[45][0-9]{2}$/.test(value)) return value;
  return UNKNOWN_CLIENT_ERROR;
}

export function normalizeSupportReference(value: unknown): string | null {
  if (typeof value !== "string" || !SUPPORT_REFERENCE.test(value)) return null;
  return value;
}
export function normalizeDiagnosticPath(value: unknown): string | null {
  if (typeof value !== "string" || !value.startsWith("/") || /[\u0000-\u001f\u007f\n\r]/.test(value)) return null;
  const path = value.split(/[?#]/, 1)[0];
  return path.length <= 512 ? path : null;
}
export function normalizeDiagnosticTimestamp(value: unknown): string {
  if (typeof value !== "string") return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) || parsed.toISOString() !== value ? "" : value;
}

export interface ErrorPageSource {
  reasonCode: string;
  supportReference: string | null;
  httpStatus: number | null;
  detail?: string;
  path?: string;
  timestamp: string;
}

export function mapProblemDetailToErrorPageSource(body: ProblemDetailBody, path?: string): ErrorPageSource {
  return {
    reasonCode: normalizeReasonCode(body.reasonCode),
    supportReference: normalizeSupportReference(body.supportReference),
    httpStatus: body.httpStatus ?? body.status ?? null,
    detail: body.detail,
    path,
    timestamp: new Date().toISOString(),
  };
}

/** ALL-5774: the public edge sends a browser navigation that an upstream refused to
 * `/error?code=HTTP_<status>&ref=<supportReference>`: the edge sees only the status, never
 * the reason code. `HTTP_401` therefore means "status 401", and the category comes from
 * the status. Anything else is not a status code. */
export function httpStatusFromErrorCode(code: string | null | undefined): number | null {
  const match = /^HTTP_([45][0-9][0-9])$/.exec(code ?? "");
  return match ? Number(match[1]) : null;
}

/** Reads `?code=` and `?ref=` (plus optional safe extras) off a browser-redirect
 * URL, per the contract with the Spring auth-redirect PR:
 *   <spa origin>/error?code=<AuthReasonCode>&ref=<supportReference>
 */
export function mapSearchParamsToErrorPageSource(params: URLSearchParams, path?: string): ErrorPageSource {
  return {
    reasonCode: normalizeReasonCode(params.get("code")),
    supportReference: normalizeSupportReference(params.get("ref")),
    httpStatus: httpStatusFromErrorCode(params.get("code")),
    path,
    timestamp: new Date().toISOString(),
  };
}

export function formatCopyDetailsBlock(source: ErrorPageSource, labels: { codeLabel: string; referenceLabel: string; timestampLabel: string; pathLabel: string }): string {
  // No reference line when the server gave none: D636 has one server-minted
  // reference per request, so the block never carries a placeholder or a
  // client-made id in its place.
  const code = normalizeReasonCode(source.reasonCode);
  const reference = normalizeSupportReference(source.supportReference);
  const timestamp = normalizeDiagnosticTimestamp(source.timestamp);
  const path = normalizeDiagnosticPath(source.path) ?? "";
  const lines = [`${labels.codeLabel}: ${code}`];
  if (reference) lines.push(`${labels.referenceLabel}: ${reference}`);
  if (timestamp) lines.push(`${labels.timestampLabel}: ${timestamp}`);
  if (path.startsWith("/")) lines.push(`${labels.pathLabel}: ${path}`);
  return lines.join("\n");
}
